import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PermissionContext = createContext({
  permissions: {},
  role: null,
  roleName: null,
  isAdmin: false,
  loading: true,
  hasPermission: () => false,
  canView: () => false,
  canCreate: () => false,
  canEdit: () => false,
  canDelete: () => false,
  refreshPermissions: () => {},
});

export const usePermissions = () => useContext(PermissionContext);

export const PermissionProvider = ({ children, user }) => {
  const [permissions, setPermissions] = useState({});
  const [role, setRole] = useState(null);
  const [roleName, setRoleName] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const permissionsHash = useRef('');

  const fetchPermissions = useCallback(async (silent = false) => {
    if (!user) {
      setPermissions({});
      setRole(null);
      setRoleName(null);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_URL}/api/roles/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;
      
      // Create a hash to detect changes
      const newHash = JSON.stringify(data.permissions);
      
      // Only update state if permissions actually changed
      if (newHash !== permissionsHash.current) {
        permissionsHash.current = newHash;
        setPermissions(data.permissions || {});
        setRole(data.role);
        setRoleName(data.role_name);
        setIsAdmin(data.is_admin || false);
        
        // If not initial load and permissions changed, could show a notification
        if (!silent && permissionsHash.current !== '' && !loading) {
          console.log('Permissions updated from server');
        }
      }
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      // Default to basic permissions based on user role
      if (user?.role === 'admin') {
        setIsAdmin(true);
      }
    } finally {
      setLoading(false);
    }
  }, [user, loading]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  // Poll for permission changes every 30 seconds (for non-admin users)
  useEffect(() => {
    if (!user || isAdmin) return;
    
    const intervalId = setInterval(() => {
      fetchPermissions(true); // Silent refresh
    }, 30000); // 30 seconds

    return () => clearInterval(intervalId);
  }, [user, isAdmin, fetchPermissions]);

  // Listen for manual refresh events (e.g., when admin updates roles)
  useEffect(() => {
    const handleRefresh = () => {
      fetchPermissions(true);
    };
    
    window.addEventListener('refresh-permissions', handleRefresh);
    return () => window.removeEventListener('refresh-permissions', handleRefresh);
  }, [fetchPermissions]);

  // Check if user has a specific permission
  const hasPermission = useCallback((module, action) => {
    if (isAdmin) return true;
    const modulePerms = permissions[module] || [];
    return modulePerms.includes(action);
  }, [permissions, isAdmin]);

  // Convenience methods
  const canView = useCallback((module) => hasPermission(module, 'view'), [hasPermission]);
  const canCreate = useCallback((module) => hasPermission(module, 'create'), [hasPermission]);
  const canEdit = useCallback((module) => hasPermission(module, 'edit'), [hasPermission]);
  const canDelete = useCallback((module) => hasPermission(module, 'delete'), [hasPermission]);

  const value = {
    permissions,
    role,
    roleName,
    isAdmin,
    loading,
    hasPermission,
    canView,
    canCreate,
    canEdit,
    canDelete,
    refreshPermissions: fetchPermissions,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

// HOC to protect components based on permissions
export const withPermission = (WrappedComponent, module, action) => {
  return function PermissionWrapper(props) {
    const { hasPermission, loading } = usePermissions();

    if (loading) {
      return null;
    }

    if (!hasPermission(module, action)) {
      return null;
    }

    return <WrappedComponent {...props} />;
  };
};

// Component to conditionally render based on permissions
export const PermissionGate = ({ module, action, children, fallback = null }) => {
  const { hasPermission, loading } = usePermissions();

  if (loading) {
    return null;
  }

  if (!hasPermission(module, action)) {
    return fallback;
  }

  return children;
};

export default PermissionContext;
